<script setup lang="ts">
import CookieConsentSettings from "@/components/common/Integration/CookieConsentSettings.vue";
import type {
  AdvancedFieldKey,
  EmbedSettingsControls,
} from "@/composables/useEmbedSettings";
import { UIInputNumber, UIRadio, UIRadioGroup } from "@gohighlevel/ghl-ui";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

const props = defineProps<{ controls: EmbedSettingsControls }>();
const { t } = useI18n();
type AdvancedOption = { label: string; value: string; unit?: string };
type AdvancedGroup = {
  key: "activation" | "trigger" | "deactivation";
  label: string;
  selected: string;
  value: string;
  valid: boolean;
  options: AdvancedOption[];
  set: (type: string, value?: string) => void;
};

// Each option keeps its own value, so typing in one box never shows up in another.
const fieldValue = (option: string): number | null => {
  const raw = props.controls.fields[option as AdvancedFieldKey];
  return raw === undefined || raw === "" ? null : Number(raw);
};
// A cleared input emits null; store an empty value instead of "null" (shown as NaN).
const toFieldValue = (value: number | null): string =>
  value == null || !Number.isFinite(value) ? "" : String(value);

const groups = computed<AdvancedGroup[]>(() => [
  {
    key: "activation",
    label: t("integrate.embed.activation"),
    selected: props.controls.settings.activationType,
    value: props.controls.settings.activationValue,
    valid: props.controls.isActivationValid.value,
    options: [
      {
        label: t("integrate.embed.activateOnVisit"),
        value: "activateOnVisit",
        unit: t("form.visit"),
      },
      {
        label: t("integrate.embed.alwaysActivated"),
        value: "alwaysActivated",
      },
    ],
    set: props.controls.setActivation,
  },
  {
    key: "trigger",
    label: t("integrate.embed.triggerType"),
    selected: props.controls.settings.triggerType,
    value: props.controls.settings.triggerValue,
    valid: props.controls.isTriggerValid.value,
    options: [
      {
        label: t("integrate.embed.showOnScrolling"),
        value: "showOnScrolling",
        unit: "%",
      },
      {
        label: t("integrate.embed.showAfter"),
        value: "showAfter",
        unit: t("common.seconds"),
      },
      { label: t("integrate.embed.alwaysShow"), value: "alwaysShow" },
    ],
    set: props.controls.setTrigger,
  },
  {
    key: "deactivation",
    label: t("integrate.embed.deactivation"),
    selected: props.controls.settings.deactivationType,
    value: props.controls.settings.deactivationValue,
    valid: props.controls.isDeactivationValid.value,
    options: [
      {
        label: t("integrate.embed.deactivateAfter"),
        value: "deactivateAfter",
        unit: t("form.times"),
      },
      { label: t("integrate.embed.leadCollected"), value: "leadCollected" },
      {
        label: t("integrate.embed.neverDeactivate"),
        value: "neverDeactivate",
      },
    ],
    set: props.controls.setDeactivation,
  },
]);
</script>

<template>
  <section data-testid="embed-advanced-settings" class="embed-advanced">
    <fieldset v-for="group in groups" :key="group.key">
      <legend class="embed-advanced__section-label">{{ group.label }}</legend>
      <UIRadioGroup
        :value="group.selected"
        :name="`embed-${group.key}`"
        class="embed-advanced__group"
        @update:value="group.set(String($event))"
      >
        <div
          v-for="option in group.options"
          :key="option.value"
          :class="[
            'embed-advanced__option',
            {
              'embed-advanced__option--wide':
                option.value === 'deactivateAfter',
            },
          ]"
          :data-testid="`embed-advanced-option-${option.value}`"
        >
          <div class="embed-advanced__option-label">
            <UIRadio
              :id="`integrate-${option.value}`"
              :label="option.label"
              :value="option.value"
            />
          </div>
          <div v-if="option.unit" class="embed-advanced__option-value">
            <UIInputNumber
              :id="`integrate-${option.value}-value`"
              :model-value="fieldValue(option.value)"
              :precision="0"
              :disabled="group.selected !== option.value"
              :show-button="false"
              :aria-label="option.label"
              @update:model-value="group.set(option.value, toFieldValue($event))"
            />
            <span>{{ option.unit }}</span>
          </div>
        </div>
      </UIRadioGroup>
      <p
        v-if="!group.valid"
        :data-testid="
          group.key === 'trigger'
            ? 'embed-validation-error'
            : `embed-validation-error-${group.key}`
        "
        class="embed-advanced__error"
      >
        {{ t("common.integratePopup.invalidInput") }}
      </p>
    </fieldset>

    <div
      data-testid="embed-cookie-consent-section"
      class="embed-advanced__cookie-section"
    >
      <CookieConsentSettings
        :enabled="controls.settings.checkForCookieConsent"
        :provider="controls.settings.cookieConsentProvider"
        @update:enabled="controls.setCookieConsent($event)"
        @update:provider="
          controls.setCookieConsent(
            controls.settings.checkForCookieConsent,
            $event
          )
        "
      />
    </div>
  </section>
</template>

<style scoped>
.embed-advanced {
  display: flex;
  flex-direction: column;
  gap: 0;
  padding: 20px;
}
.embed-advanced fieldset {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 0;
  padding: 18px 0;
  border: 0;
  border-bottom: 1px solid var(--gray-200, #eaecf0);
}
.embed-advanced fieldset:last-of-type {
  border-bottom: 0;
}
.embed-advanced__cookie-section {
  padding-top: 18px;
  border-top: 1px solid var(--gray-200, #eaecf0);
}
.embed-advanced legend {
  margin-bottom: 4px;
  color: var(--gray-700, #344054);
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
}
.embed-advanced__group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.embed-advanced__option {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  min-height: 28px;
  align-items: center;
  gap: 8px;
  color: var(--gray-700, #344054);
  font-size: 14px;
}
.embed-advanced__option-label {
  min-width: 0;
}
.embed-advanced__option-value {
  display: flex;
  align-items: center;
  gap: 8px;
}
.embed-advanced__option--wide {
  grid-template-columns: minmax(0, 1fr);
}
.embed-advanced__option--wide .embed-advanced__option-value {
  margin-left: 26px;
}
.embed-advanced__option :deep(.n-radio),
.embed-advanced__option :deep(.n-radio__label) {
  min-width: 0;
  white-space: nowrap;
}
.embed-advanced__option :deep(.hl-input-number) {
  width: 64px !important;
  background: var(--base-white, #fff) !important;
}
.embed-advanced__error {
  margin: 0;
  color: var(--error-600, #d92d20);
  font-size: 13px;
}
</style>
